import * as Sentry from "@sentry/nextjs";

const STORAGE_KEYS = {
  DEVICE_TOKEN: "mfa_device_token",
  DEVICE_TOKEN_EXPIRES: "mfa_device_token_expires",
} as const;

/**
 * Stores the device token and its expiration timestamp in localStorage
 * @param token - The device token to store
 * @param expiresAt - ISO 8601 timestamp when the token expires
 */
export const storeDeviceToken = (token: string, expiresAt: string): void => {
  try {
    localStorage.setItem(STORAGE_KEYS.DEVICE_TOKEN, token);
    localStorage.setItem(STORAGE_KEYS.DEVICE_TOKEN_EXPIRES, expiresAt);
  } catch (error) {
    Sentry.captureException(error);
  }
};

/**
 * Retrieves the device token from localStorage
 * @returns The device token or null if not found
 */
export const getDeviceToken = (): string | null => {
  try {
    return localStorage.getItem(STORAGE_KEYS.DEVICE_TOKEN);
  } catch (error) {
    Sentry.captureException(error);
    return null;
  }
};

/**
 * Retrieves the device token expiration timestamp from localStorage
 * @returns The expiration timestamp or null if not found
 */
export const getDeviceTokenExpiration = (): string | null => {
  try {
    return localStorage.getItem(STORAGE_KEYS.DEVICE_TOKEN_EXPIRES);
  } catch (error) {
    Sentry.captureException(error);
    return null;
  }
};

/**
 * Checks if the stored device token is still valid (not expired)
 * @returns true if token exists and is not expired, false otherwise
 */
export const isDeviceTokenValid = (): boolean => {
  try {
    const token = getDeviceToken();
    const expiresAt = getDeviceTokenExpiration();

    if (!token || !expiresAt) {
      return false;
    }

    const expirationDate = new Date(expiresAt);
    const now = new Date();

    return expirationDate > now;
  } catch (error) {
    Sentry.captureException(error);
    return false;
  }
};

/**
 * Clears the device token and expiration from localStorage
 * Should be called on logout or when token is revoked
 */
export const clearDeviceToken = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEYS.DEVICE_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.DEVICE_TOKEN_EXPIRES);
  } catch (error) {
    Sentry.captureException(error);
  }
};
